import { useCallback, useState } from 'react';

import AllowedHostsList from 'dashboard/components/AllowedHostsList';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { AddAllowedHostFormData } from 'dashboard/forms/FormAddAllowedHost';
import { AddAllowedUriSchemeFormData } from 'dashboard/forms/FormAddAllowedUriScheme';
import FormOrganizationSubdomain, {
  OrganizationSubdomainFormData,
} from 'dashboard/forms/FormOrganizationSubdomain';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import AllowedUriSchemesList from 'dashboard/views/DashboardGettingStartedEmbeddedFlowView/AllowedUriSchemesList';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

interface DashboardGettingStartedHostedViewProps {
  addAllowedHost: (values: AddAllowedHostFormData) => Promise<void>;
  addAllowedUriScheme: ({ uriScheme }: AddAllowedUriSchemeFormData) => Promise<void>;
  allowedHosts: string[];
  allowedUriSchemes: string[];
  removeAllowedHost: (hostIndex: number) => Promise<void>;
  removeAllowedUriScheme: (uriSchemeIndex: number) => Promise<void>;
  subdomain: string | null;
  updateOrganizationSubdomain: (values: OrganizationSubdomainFormData) => Promise<void>;
}

export default function DashboardInquiriesDomainManagerView({
  addAllowedHost,
  addAllowedUriScheme,
  allowedHosts,
  allowedUriSchemes,
  removeAllowedHost,
  removeAllowedUriScheme,
  subdomain,
  updateOrganizationSubdomain,
}: DashboardGettingStartedHostedViewProps) {
  const [hostToRemoveIndex, setHostToRemoveIndex] = useState<number | undefined>(undefined);

  const hostToRemove = hostToRemoveIndex != null ? allowedHosts[hostToRemoveIndex] : null;

  const handleRemoveAllowedHost = (hostIndex: number) => {
    setHostToRemoveIndex(hostIndex);
  };

  const [uriSchemeToRemoveIndex, setUriSchemeToRemoveIndex] = useState<number | undefined>(
    undefined,
  );

  const uriSchemeToRemove =
    uriSchemeToRemoveIndex != null ? allowedUriSchemes[uriSchemeToRemoveIndex] : null;

  const handleRemoveAllowedUriScheme = (uriSchemeIndex: number) => {
    setUriSchemeToRemoveIndex(uriSchemeIndex);
  };

  const hideConfirmRemoveUriSchemeModal = useCallback(
    () => setUriSchemeToRemoveIndex(undefined),
    [],
  );

  const hideConfirmRemoveHostModal = useCallback(() => setHostToRemoveIndex(undefined), []);

  return (
    <>
      <Container>
        <Header title='Inquiries Domain Manager' />
        <Section>
          <Card>
            <Card.Body>
              <Card.Title>Subdomain</Card.Title>
              <Paragraph color='primary'>
                Your hosted flow integration method can be hosted on a withpersona.com subdomain.
                The URL has two parts: a subdomain name you chose when you set up your account,
                followed by <b>withpersona.com</b>. For example, <b>mycompany.withpersona.com</b>.
                You can change this subdomain below.
              </Paragraph>
              <FormOrganizationSubdomain
                initialValues={{ subdomain: subdomain || '' }}
                onSubmit={updateOrganizationSubdomain}
              />
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title>Allowed Domains</Card.Title>
              <Paragraph>
                The domain allowlist is shared across <b>all environments</b> in your organization.
                Read more about the domain allowlist{' '}
                <a
                  href='https://docs.withpersona.com/docs/hosted-flow-security#allowed-domains'
                  target='_blank'
                  rel='noreferrer'
                >
                  here
                </a>
                .
              </Paragraph>
              <Paragraph color='primary'>
                This allowlist controls which domains are able to embed your flow via Embedded Flow,
                and what URLs are valid redirect targets after completion of a Hosted Flow. A
                wildcard character (*) can be used to specify subdomains.
              </Paragraph>
              <Paragraph color='primary'>
                If this list is empty, all domains are allowed. Otherwise, Persona will only allow
                redirects or embeds for domains in this list.
              </Paragraph>
              <Spacer height={2} />
              <AllowedHostsList
                allowedHosts={allowedHosts}
                addAllowedHost={addAllowedHost}
                removeAllowedHost={handleRemoveAllowedHost}
              />
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <Card.Title>Allowed URI Schemes</Card.Title>
              <Paragraph color='primary'>
                This allowlist allows you to specify custom URI schemes for redirect targets after
                completion of a Hosted Flow. This can be used to implement deep linking for mobile
                integrations.
              </Paragraph>
              <Paragraph color='primary'>
                If this list is empty, all URLs with the URI scheme http or https are allowed.
              </Paragraph>
              <Spacer height={2} />
              <FormSection>
                <AllowedUriSchemesList
                  allowedUriSchemes={allowedUriSchemes}
                  addAllowedUriScheme={addAllowedUriScheme}
                  removeAllowedUriScheme={handleRemoveAllowedUriScheme}
                />
              </FormSection>
            </Card.Body>
          </Card>
        </Section>
      </Container>

      <ModalWrapper isOpen={hostToRemove != null} handleCloseModal={hideConfirmRemoveHostModal}>
        <ConfirmationModal
          cancelAction={hideConfirmRemoveHostModal}
          confirmAction={() => {
            if (!(hostToRemoveIndex == null)) {
              return removeAllowedHost(hostToRemoveIndex);
            }
          }}
          header='Remove allowed domain?'
          confirmActionText='Remove domain'
          cancelActionText='Cancel'
        >
          Once <b>{hostToRemove}</b> is removed, customers in the middle of completing verifications
          on this domain will be unable to finish. <b>This affects all environments.</b>
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper
        isOpen={uriSchemeToRemove != null}
        handleCloseModal={hideConfirmRemoveUriSchemeModal}
      >
        <ConfirmationModal
          cancelAction={hideConfirmRemoveUriSchemeModal}
          confirmAction={() => {
            if (!(uriSchemeToRemoveIndex == null)) {
              return removeAllowedUriScheme(uriSchemeToRemoveIndex);
            }
          }}
          header='Remove allowed URI scheme?'
          confirmActionText='Remove URI scheme'
          cancelActionText='Cancel'
        >
          Once <b>{uriSchemeToRemove}</b> is removed, customers who would be redirected to this URI
          scheme will fail to be redirected.
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
}
